import { useTranslation } from 'react-i18next';
import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import LabeledLabelStatus from 'components/molecules/labeledStatus/LabeledLabelStatus';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { t } from 'i18next';
import {
  PiBuildingOffice,
  PiIdentificationCard,
  PiUserCircle,
  PiWarning,
} from 'react-icons/pi';
import { useNavigate } from 'react-router';
import { useGetLabelListInfinite } from 'services/labels';
import {
  CUSTOMER_TYPE,
  IGetLabelListInfiniteParams,
  ILabelResponse,
  LABEL_STATUS,
  RESIDENCE_CERTIFICATE_EXPIRATION_STATUS,
} from 'services/labels/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { magellanColumns } from 'types/components/table/table.types';
import {
  booleanSelectOptions,
  useEnumToSelectOptions,
} from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';
import { useSearchById, useSetSearchById } from 'store/state';

enum LABEL_COLUMNS {
  labelName,
  fullname,
  contact,
  customerType,
  status,
  residenceCertificateExpirationStatus,
}

const initialValues: IGetLabelListInfiniteParams = {
  labelName: '',
  personalId: '',
  companyId: '',
  email: '',
  status: undefined,
  showExpiring: undefined,
  customerTypeId: undefined,
  hasContracts: undefined,
};

const Labels = () => {
  const { t } = useTranslation();
  usePermissionGuard('LabelPermissionsLIST**');
  const hasPermission = useHasPermission();

  const columnsVisibility = useTableColumnsVisibility('Labels2', LABEL_COLUMNS);
  const navigate = useNavigate();
  const i18nextPrefix = 'labels';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || initialValues;

  const { data, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetLabelListInfinite({ ...filters, itemCount: 25 });
  const tableData = getNodes<ILabelResponse>(data?.pages.flat());

  const hasDetailPermission = hasPermission('LabelPermissionsLIST**', 16);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/finances`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('label.title.list')}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  filters={filters}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={LABEL_COLUMNS}
                  i18nextPrefix='label'
                  isClear
                  visibility={columnsVisibility}
                />
              </div>
              {hasPermission('LabelPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create/finances', { replace: false })}>
                  <Button.Text
                  text={t('button.create-what', { what: t('label.what') })}
                />
                  <Button.Icon icon={<PiIdentificationCard />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IGetLabelListInfiniteParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IGetLabelListInfiniteParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(LABEL_STATUS, 'enum.LABEL_STATUS', {
                      includeLabelStatusBanned: true,
                    })}
                  />
                  <FormInput.Text
                    name='labelName'
                    label={t('input.label.name')}
                  />
                  <FormInput.Text name='email' label={t('input.label.email')} />
                  <FormInput.Text
                    name='personalId'
                    label={t('input.label.personalId')}
                  />
                  <FormInput.Text
                    name='companyId'
                    label={t('input.label.companyId')}
                  />
                  <FormInput.Select
                    name='showExpiring'
                    label={t('input.label.showExpiring')}
                    options={[
                      { label: t('input.option.yes'), value: true },
                      { label: t('input.option.no'), value: false },
                    ]}
                  />
                  <FormInput.Select
                    name='customerTypeId'
                    label={t('input.label.customerType')}
                    options={getSelectOptions(CUSTOMER_TYPE, 'enum.CUSTOMER_TYPE')}
                  />
                  <FormInput.Select
                    name='hasContracts'
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.LABEL_STATUS.${LABEL_STATUS[data.value as LABEL_STATUS]}`)
                    : data.key === 'customerTypeId'
                    ? t(`enum.CUSTOMER_TYPE.${CUSTOMER_TYPE[data.value as CUSTOMER_TYPE]}`)
                    : data.key === 'showExpiring'
                    ? data.value !== undefined
                      ? `${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        }`
                      : undefined
                    : data.key === 'hasContracts'
                    ? data.value !== undefined
                      ? `${t('input.label.hasContracts')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={magellanColumns<ILabelResponse>([
          {
            label: t('label.table.labelName'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.labelName),
            minWidth: 200,
          },
          {
            label: t('label.table.fullname'),
            renderCell: ({ firstName, lastName }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    firstName && lastName
                      ? `${firstName} ${lastName}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.fullname),
          },
          {
            label: t('label.table.contact'),
            renderCell: ({ email, phoneNumberNumber, phoneNumberPrefix }) => (
              <TableItem className='gap-2'>
                <TableItem.Text text={email} />
                {phoneNumberNumber && phoneNumberPrefix ? (
                  <TableItem.Text
                    text={`${phoneNumberPrefix} ${phoneNumberNumber}`}
                    isSupplementary
                  />
                ) : null}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.contact),
          },
          {
            label: t('label.table.customerType'),
            renderCell: ({ customerTypeId }) => (
              <TableItem justifyPosition='center'>
                {customerTypeId === CUSTOMER_TYPE.Company && (
                  <TableItem.Label color='blue' size='compact'>
                    <PiBuildingOffice />
                    {t('input.label.company')}
                  </TableItem.Label>
                )}
                {customerTypeId === CUSTOMER_TYPE.Physical && (
                  <TableItem.Label color='black' size='compact'>
                    <PiUserCircle />
                    {t('input.label.physicalPerson')}
                  </TableItem.Label>
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.customerType),
          },
          {
            label: t('label.table.status'),
            renderCell: ({ labelStatus }) => (
              <TableItem justifyPosition='center'>
                <LabeledLabelStatus status={labelStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.status),
            minWidth: 140,
          },
          {
            label: "",
            minWidth: 140,
            renderCell: ({ residenceCertificateExpirationStatus }) => (
              <TableItem justifyPosition='center'>
                {residenceCertificateExpirationStatus?.id === RESIDENCE_CERTIFICATE_EXPIRATION_STATUS.ExpiringSoon && (
                  <Button
                    onlyIcon
                    isClear
                    variant={BUTTON_VARIANT.warning}>
                    <Button.Description description={t('button.info')} />

                    <Button.Icon icon={<PiWarning />} />
                  </Button>
                )}
                {residenceCertificateExpirationStatus?.id === RESIDENCE_CERTIFICATE_EXPIRATION_STATUS.Expired && (
                  <Button
                    onlyIcon
                    isClear
                    variant={BUTTON_VARIANT.remove}>
                    <Button.Description description={t('button.info')} />
                    <Button.Icon icon={<PiWarning />} />
                  </Button>
                )}
              </TableItem>
            ),
            hide: columnsVisibility.hide(LABEL_COLUMNS.residenceCertificateExpirationStatus),
          },
        ])}
        displayMode
      />
    </TopbarPage>
  );
};

export default Labels;
